import Cookies from "js-cookie";
import useSWR from "swr";

import {apiFetcher} from "@lib/fetcher";

type Envelope<T> = {
    status: "ok" | "partial" | "error";
    data: T | null;
    warnings?: string[];
    errors?: string[];
};

export interface StaffingHeadlineCheck {
    name: string;
    expected: string;
    actual: string | null;
    passed: boolean;
}

export interface StaffingUploadPayload {
    promoted: boolean;
    run: StaffingImportRun | null;
    checks?: StaffingHeadlineCheck[];
}

export interface StaffingUploadResult {
    ok: boolean;
    payload: StaffingUploadPayload | null;
    warnings: string[];
    errors: string[];
}

export interface StaffingImportRun {
    id: string;
    file_type: string;
    filename: string;
    status: string;
    is_active: boolean;
    validation_passed: boolean;
    row_counts: Record<string, unknown>;
    validation_totals: Record<string, unknown>;
    warnings: string[];
    errors: string[];
    started_at: string | null;
    completed_at: string | null;
    promoted_at: string | null;
}

export interface StaffingImportStatusRun {
    file_type: string;
    latest_run: StaffingImportRun | null;
    active_run: StaffingImportRun | null;
    issue_summary: {block: number; warn: number; info: number};
}

export interface StaffingImportStatusData {
    initialized: boolean;
    runs: StaffingImportStatusRun[];
}

export interface StaffingAvailability {
    enabled: boolean;
    initialized: boolean;
    isLoading: boolean;
    status?: StaffingImportStatusData;
}

export interface StaffingActualsGroup {
    group?: string;
    client?: string;
    service_location?: string;
    pay_level?: string;
    shift_type?: string;
    parent?: string | null;
    sector?: string | null;
    fy_year?: number | null;
    fy_week?: number | null;
    revenue: string;
    cost: string;
    profit: string;
    margin_pct: string;
    row_count: number;
    invoice_count: number;
}

export interface StaffingActualsMarginData {
    group_by: string[];
    filters?: Record<string, string>;
    groups: StaffingActualsGroup[];
    active_run: StaffingImportRun | null;
}

export interface StaffingFyCalendarBuildResult {
    ok: boolean;
    built: boolean;
    fy_range?: [number, number];
    updated?: number;
    missing_date?: number;
    outside_spine?: [number, number][];
    errors: string[];
}

export interface StaffingDetailRow {
    source_row_number: number;
    invoice_no: string;
    tsheet_no: string;
    shift_date: string | null;
    invoice_date: string | null;
    client: string;
    service_location: string;
    pay_level: string;
    qualification: string;
    shift_type: string;
    day: string;
    hours: string;
    invoice_rate: string;
    pay_rate: string;
    invoice_total: string;
    gross_wages: string;
    superannuation: string;
    payroll_tax: string;
    workcover: string;
    profit: string;
    margin_pct: string;
}

export interface StaffingDetailReconciliation {
    detail_total: string;
    detail_profit: string;
    detail_count: number;
}

export interface StaffingActualsDetailData {
    filters: Record<string, string>;
    page: number;
    page_size: number;
    total_rows: number;
    reconciliation: StaffingDetailReconciliation;
    rows: StaffingDetailRow[];
}

export interface StaffingClientSiteCoverageData {
    has_mapping: boolean;
    mapping_run?: StaffingImportRun | null;
    totals?: {
        mapping_sites: number;
        actuals_sites: number;
        eoh_booking_sites: number;
        mapped_actuals_sites: number;
        mapped_actuals_pct: number | null;
    };
    gaps?: {
        missing_xero_contact: number;
        missing_anchor_flag: number;
        missing_rate_card_tier: number;
    };
    unmapped_gross_margin_sites?: string[];
    unmapped_eoh_booking_sites?: string[];
    stale_mapping_sites?: string[];
}

export interface StaffingRateCardSummaryData {
    has_rate_card: boolean;
    active_run?: StaffingImportRun | null;
    totals?: {
        rows: number;
        rate_line_rows?: number;
        distinct_rate_tables: number;
        distinct_source_files: number;
        distinct_pay_levels: number;
        distinct_shift_types: number;
        missing_or_zero_pay_rates: number;
        missing_or_zero_invoice_rates: number;
    };
    top_rate_tables?: {
        rate_table_name: string;
        row_count: number;
    }[];
    warnings?: string[];
    data_gaps?: string[];
}

export const STAFFING_DETAIL_PAGE_SIZE = 50;

async function statusFetcher(url: string): Promise<StaffingAvailability> {
    const response = await fetch(url, {
        method: "GET",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
    });
    let json: Envelope<StaffingImportStatusData> | null = null;
    try {
        json = await response.json();
    } catch {
        json = null;
    }

    if (response.status === 403) {
        return {enabled: false, initialized: false, isLoading: false};
    }
    if (response.status === 503) {
        return {
            enabled: true,
            initialized: false,
            isLoading: false,
            status: json?.data ?? {initialized: false, runs: []},
        };
    }
    if (!response.ok || !json || json.status === "error") {
        return {enabled: false, initialized: false, isLoading: false};
    }
    return {
        enabled: true,
        initialized: json.data?.initialized ?? true,
        isLoading: false,
        status: json.data ?? {initialized: true, runs: []},
    };
}

export function useStaffingMarginAvailability(): StaffingAvailability {
    const {data, isLoading} = useSWR<StaffingAvailability>(
        "/api/staffing-margin/import/status/?file_type=gross_margin&availability=1",
        statusFetcher,
        {revalidateOnFocus: false},
    );
    return data ?? {enabled: false, initialized: false, isLoading};
}

export function useStaffingMarginImportStatus() {
    const {data, error, isLoading, mutate} = useSWR<StaffingImportStatusData>(
        "/api/staffing-margin/import/status/?file_type=gross_margin",
        apiFetcher,
    );
    return {data, error, isLoading, mutate};
}

export function useStaffingActualsMargin(
    groupBy: string,
    filters?: Record<string, string>,
) {
    const params = new URLSearchParams({group_by: groupBy});
    for (const [key, value] of Object.entries(filters ?? {})) {
        if (value) params.set(key, value);
    }
    const {data, error, isLoading, mutate} = useSWR<StaffingActualsMarginData>(
        `/api/staffing-margin/actuals/margin/?${params.toString()}`,
        apiFetcher,
    );
    return {data, error, isLoading, mutate};
}

export function useStaffingActualsDetail(client: string | null, page: number) {
    const key =
        client === null
            ? null
            : `/api/staffing-margin/actuals/detail/?client=${encodeURIComponent(client)}`
              + `&page=${page}&page_size=${STAFFING_DETAIL_PAGE_SIZE}`;
    const {data, error, isLoading} = useSWR<StaffingActualsDetailData>(key, apiFetcher, {
        keepPreviousData: true,
        revalidateOnFocus: false,
    });
    return {data, error, isLoading};
}

export function useStaffingClientSiteCoverage(enabled: boolean) {
    const {data, error, isLoading, mutate} = useSWR<StaffingClientSiteCoverageData>(
        enabled ? "/api/staffing-margin/client-site-map/coverage/" : null,
        apiFetcher,
        {revalidateOnFocus: false},
    );
    return {data, error, isLoading, mutate};
}

export function useStaffingRateCardSummary(enabled: boolean) {
    const {data, error, isLoading, mutate} = useSWR<StaffingRateCardSummaryData>(
        enabled ? "/api/staffing-margin/rate-card/summary/" : null,
        apiFetcher,
        {revalidateOnFocus: false},
    );
    return {data, error, isLoading, mutate};
}

export async function uploadClientSiteMapCsv(file: File): Promise<StaffingUploadResult> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const formData = new FormData();
    formData.append("file", file);

    let response: Response;
    try {
        response = await fetch("/api/staffing-margin/client-site-map/upload/", {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            body: formData,
        });
    } catch (e) {
        return {
            ok: false,
            payload: null,
            warnings: [],
            errors: [e instanceof Error ? e.message : "network error"],
        };
    }

    let json: Envelope<StaffingUploadPayload> | null = null;
    try {
        json = await response.json();
    } catch {
        json = null;
    }

    if (response.status === 403) {
        return {
            ok: false,
            payload: null,
            warnings: [],
            errors: ["Permission denied — Alseasons workbench is not enabled for this account."],
        };
    }

    const payload = json?.data ?? null;
    const ok = response.ok && json?.status === "ok";
    return {
        ok,
        payload,
        warnings: json?.warnings ?? [],
        errors: json?.errors ?? (ok ? [] : [`Upload failed (HTTP ${response.status})`]),
    };
}

export async function uploadRateCardCsv(file: File): Promise<StaffingUploadResult> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const formData = new FormData();
    formData.append("file", file);

    let response: Response;
    try {
        response = await fetch("/api/staffing-margin/rate-card/upload/", {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            body: formData,
        });
    } catch (e) {
        return {
            ok: false,
            payload: null,
            warnings: [],
            errors: [e instanceof Error ? e.message : "network error"],
        };
    }

    let json: Envelope<StaffingUploadPayload> | null = null;
    try {
        json = await response.json();
    } catch {
        json = null;
    }

    if (response.status === 403) {
        return {
            ok: false,
            payload: null,
            warnings: [],
            errors: ["Permission denied — Alseasons workbench is not enabled for this account."],
        };
    }

    const payload = json?.data ?? null;
    const ok = response.ok && json?.status === "ok";
    return {
        ok,
        payload,
        warnings: json?.warnings ?? [],
        errors: json?.errors ?? (ok ? [] : [`Upload failed (HTTP ${response.status})`]),
    };
}

export async function buildFyCalendar(): Promise<StaffingFyCalendarBuildResult> {
    const csrfToken = Cookies.get("csrftoken") || "";
    let response: Response;
    try {
        response = await fetch("/api/staffing-margin/fy-calendar/build/", {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken, "Content-Type": "application/json"},
            credentials: "include",
        });
    } catch (e) {
        return {
            ok: false,
            built: false,
            errors: [e instanceof Error ? e.message : "network error"],
        };
    }

    let json: Envelope<{
        built: boolean;
        fy_range?: [number, number];
        backfill?: {updated: number; missing_date: number; outside_spine: [number, number][]};
    }> | null = null;
    try {
        json = await response.json();
    } catch {
        json = null;
    }

    const data = json?.data ?? null;
    const ok = response.ok && (json?.status === "ok" || json?.status === "partial");
    return {
        ok,
        built: data?.built ?? false,
        fy_range: data?.fy_range,
        updated: data?.backfill?.updated,
        missing_date: data?.backfill?.missing_date,
        outside_spine: data?.backfill?.outside_spine,
        errors: json?.errors ?? (ok ? [] : [`Build failed (HTTP ${response.status})`]),
    };
}

export async function uploadGrossMarginCsv(file: File): Promise<StaffingUploadResult> {
    const csrfToken = Cookies.get("csrftoken") || "";
    const formData = new FormData();
    formData.append("file", file);

    let response: Response;
    try {
        response = await fetch("/api/staffing-margin/import/gross-margin/", {
            method: "POST",
            headers: {"X-CSRFToken": csrfToken},
            credentials: "include",
            body: formData,
        });
    } catch (e) {
        return {
            ok: false,
            payload: null,
            warnings: [],
            errors: [e instanceof Error ? e.message : "network error"],
        };
    }

    let json: Envelope<StaffingUploadPayload> | null = null;
    try {
        json = await response.json();
    } catch {
        json = null;
    }

    if (response.status === 403) {
        return {
            ok: false,
            payload: null,
            warnings: [],
            errors: ["Permission denied — Alseasons workbench is not enabled for this account."],
        };
    }

    const payload = json?.data ?? null;
    const ok = response.ok && json?.status === "ok";
    return {
        ok,
        payload,
        warnings: json?.warnings ?? [],
        errors: json?.errors ?? (ok ? [] : [`Upload failed (HTTP ${response.status})`]),
    };
}
